import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { messagesApi, emailConnectionsApi } from "../services/api";
import { useUserDataContext } from "../context/UserDataContext";
import { errorMessage } from "../components/ui/Toast";

// ── Types ────────────────────────────────────────────────────────────

export interface ComposeAttachment {
  key: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

export interface ComposeState {
  /** Unique key so React can remount per-draft */
  draftId: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
  attachments: ComposeAttachment[];
  /** Conversation to post the message against */
  conversationId: string | null;
  /** Email connection to send from */
  emailConnectionId: string | null;
  /** Reply-in-thread */
  replyToMessageId: string | null;
  replyToSubject: string | null;
  /** Client associated with this conversation (for document picker) */
  clientId: string | null;
}

export type PopupMode = "closed" | "minimized" | "expanded";

interface EmailComposeContextValue {
  /** Current popup visibility state */
  mode: PopupMode;
  /** Full compose draft state */
  draft: ComposeState;
  /** Available email connections (loaded once) */
  emailConnections: Array<{ _id: string; mailbox: { email: string; displayName?: string }; provider: string }>;
  /** True while the send request is in flight */
  sending: boolean;
  /** Why the last send failed (draft is kept); null when there is nothing to show */
  sendError: string | null;
  clearSendError: () => void;
  /** True while an attachment is uploading */
  uploadingAttachment: boolean;

  // ── Actions ──
  /** Open a fresh compose popup */
  openCompose: (opts?: {
    to?: string[];
    subject?: string;
    conversationId?: string;
    emailConnectionId?: string;
    clientId?: string;
  }) => void;
  /** Open compose as a reply to a specific message */
  openReply: (opts: {
    conversationId: string;
    replyToMessageId: string;
    subject: string;
    to?: string[];
    emailConnectionId?: string;
    clientId?: string;
  }) => void;
  /** Minimize the popup to a small title bar */
  minimize: () => void;
  /** Expand a minimized popup */
  expand: () => void;
  /** Close + discard the draft */
  close: () => void;
  /** Update a single field on the draft */
  updateDraft: (patch: Partial<ComposeState>) => void;
  /** Upload attachment files */
  uploadAttachments: (files: FileList) => Promise<void>;
  /** Remove an attachment by index */
  removeAttachment: (index: number) => void;
  /** Send the email */
  send: () => Promise<boolean>;
  /** Callback registry  -  pages can subscribe to "sent" events */
  onSent: (cb: (conversationId: string) => void) => () => void;
}

const EmailComposeContext = createContext<EmailComposeContextValue | null>(null);

export const useEmailCompose = () => {
  const ctx = useContext(EmailComposeContext);
  if (!ctx) throw new Error("useEmailCompose must be used within EmailComposeProvider");
  return ctx;
};

// ── Default empty draft ──────────────────────────────────────────────

const emptyDraft = (): ComposeState => ({
  draftId: crypto.randomUUID?.() || `${Date.now()}`,
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  bodyHtml: "",
  attachments: [],
  conversationId: null,
  emailConnectionId: null,
  replyToMessageId: null,
  replyToSubject: null,
  clientId: null,
});

// ── Provider ─────────────────────────────────────────────────────────

export const EmailComposeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userData, firmData } = useUserDataContext();

  const [mode, setMode] = useState<PopupMode>("closed");
  const [draft, setDraft] = useState<ComposeState>(emptyDraft());
  const [emailConnections, setEmailConnections] = useState<Array<{ _id: string; mailbox: { email: string; displayName?: string }; provider: string }>>([]);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const clearSendError = useCallback(() => setSendError(null), []);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // Callback registry for "sent" events
  const sentCallbacksRef = useRef<Set<(conversationId: string) => void>>(new Set());

  // ── Signature builder ──────────────────────────────────────────────

  const buildSignatureHtml = useCallback(() => {
    const name = userData.name || "Your Name";
    const title = userData.title || "";
    const firm = firmData.name || "";
    const phone = userData.phone || (firmData as any).primaryPhone || "";
    const lines = [
      `<strong style="font-size: 14px; color: #0F5132;">${name}</strong>`,
      title ? `<span style="color: #475569; font-size: 12px;">${title}</span>` : "",
      firm ? `<span style="color: #64748b; font-size: 12px;">${firm}</span>` : "",
      phone ? `<span style="color: #64748b; font-size: 12px;">${phone}</span>` : "",
    ].filter(Boolean);

    const savedSig = userData.signatureBlock;
    let effectiveSig: string;
    if (savedSig) {
      try { JSON.parse(savedSig); effectiveSig = ""; } catch { effectiveSig = savedSig; }
    } else {
      effectiveSig = "";
    }
    if (!effectiveSig) {
      effectiveSig = `<div style="font-family: 'Helvetica Neue', Arial, sans-serif; line-height: 1.5; border-left: 2px solid #0F5132; padding-left: 12px;">${lines.join("<br/>")}</div><div style="margin-top: 8px; font-size: 10px; color: #94a3b8; font-style: italic;">This email and any attachments are privileged and confidential.</div>`;
    }

    return `<br/><div style="margin-top: 16px; padding-top: 12px; border-top: 1px solid #e2e8f0;">${effectiveSig}</div>`;
  }, [userData, firmData]);

  // ── Load email connections (once per open) ──────────────────────────

  const loadConnections = useCallback(async () => {
    if (connectionsLoaded) return;
    try {
      const res = await emailConnectionsApi.list();
      const active = (res.data?.data || res.data || []).filter(
        (c: any) => c.status?.state === "active" && c.auth?.oauth?.enabled
      );
      setEmailConnections(active);
      setConnectionsLoaded(true);
    } catch {
      setConnectionsLoaded(true);
    }
  }, [connectionsLoaded]);

  // ── Actions ────────────────────────────────────────────────────────

  const openCompose = useCallback(
    (opts?: { to?: string[]; subject?: string; conversationId?: string; emailConnectionId?: string; clientId?: string }) => {
      loadConnections();
      const sig = buildSignatureHtml();
      const d = emptyDraft();
      d.bodyHtml = sig;
      if (opts?.to) d.to = opts.to;
      if (opts?.subject) d.subject = opts.subject;
      if (opts?.conversationId) d.conversationId = opts.conversationId;
      if (opts?.emailConnectionId) d.emailConnectionId = opts.emailConnectionId;
      if (opts?.clientId) d.clientId = opts.clientId;
      setDraft(d);
      setSendError(null);
      setMode("expanded");
    },
    [loadConnections, buildSignatureHtml]
  );

  const openReply = useCallback(
    (opts: { conversationId: string; replyToMessageId: string; subject: string; to?: string[]; emailConnectionId?: string; clientId?: string }) => {
      loadConnections();
      const sig = buildSignatureHtml();
      const d = emptyDraft();
      d.bodyHtml = sig;
      d.conversationId = opts.conversationId;
      d.replyToMessageId = opts.replyToMessageId;
      d.replyToSubject = opts.subject;
      d.subject = opts.subject.startsWith("Re:") ? opts.subject : `Re: ${opts.subject}`;
      if (opts.to) d.to = opts.to;
      if (opts.emailConnectionId) d.emailConnectionId = opts.emailConnectionId;
      if (opts.clientId) d.clientId = opts.clientId;
      setDraft(d);
      setSendError(null);
      setMode("expanded");
    },
    [loadConnections, buildSignatureHtml]
  );

  const minimize = useCallback(() => setMode("minimized"), []);
  const expand = useCallback(() => setMode("expanded"), []);

  const close = useCallback(() => {
    setMode("closed");
    setSendError(null);
    setDraft(emptyDraft());
  }, []);

  const updateDraft = useCallback((patch: Partial<ComposeState>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  // ── Attachment upload ──────────────────────────────────────────────

  const uploadAttachments = useCallback(async (files: FileList) => {
    setUploadingAttachment(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await messagesApi.uploadAttachment(formData);
        setDraft((prev) => ({
          ...prev,
          attachments: [...prev.attachments, res.data],
        }));
      }
    } catch (err) {
      console.error("Failed to upload attachment:", err);
    } finally {
      setUploadingAttachment(false);
    }
  }, []);

  const removeAttachment = useCallback((index: number) => {
    setDraft((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  }, []);

  // ── Send ───────────────────────────────────────────────────────────

  const send = useCallback(async (): Promise<boolean> => {
    if (!draft.conversationId || !draft.bodyHtml.trim() || draft.to.length === 0) return false;
    setSending(true);
    setSendError(null);

    try {
      const body = draft.bodyHtml.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      const bodyHtml = `<div style="font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 14px; color: #1e293b;">${draft.bodyHtml}</div>`;

      await messagesApi.send({
        conversationId: draft.conversationId,
        channel: "email",
        to: draft.to.length > 0 ? draft.to : [],
        body,
        bodyHtml,
        subject: draft.subject || undefined,
        emailConnectionId: draft.emailConnectionId || undefined,
        inReplyToMessageId: draft.replyToMessageId || undefined,
        cc: draft.cc.length ? draft.cc : undefined,
        bcc: draft.bcc.length ? draft.bcc : undefined,
        attachments: draft.attachments.length ? draft.attachments : undefined,
      });

      // Notify listeners
      const convId = draft.conversationId;
      sentCallbacksRef.current.forEach((cb) => cb(convId));

      // Reset
      setMode("closed");
      setDraft(emptyDraft());
      return true;
    } catch (err) {
      console.error("Failed to send email:", err);
      setSendError(errorMessage(err, "Couldn't send the email. Your draft is still here, try again."));
      return false;
    } finally {
      setSending(false);
    }
  }, [draft]);

  // ── Sent callback registry ─────────────────────────────────────────

  const onSent = useCallback((cb: (conversationId: string) => void) => {
    sentCallbacksRef.current.add(cb);
    return () => {
      sentCallbacksRef.current.delete(cb);
    };
  }, []);

  return (
    <EmailComposeContext.Provider
      value={{
        mode,
        draft,
        emailConnections,
        sending,
        sendError,
        clearSendError,
        uploadingAttachment,
        openCompose,
        openReply,
        minimize,
        expand,
        close,
        updateDraft,
        uploadAttachments,
        removeAttachment,
        send,
        onSent,
      }}
    >
      {children}
    </EmailComposeContext.Provider>
  );
};
